import Head from 'next/head';
import { useRouter } from 'next/router';
import { type PropsWithChildren, useEffect } from 'react';
import { connect } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import { trackHotelPage } from '@/components/tracking/FinnAnalytics';
import type { Filters, Hotel, PaleState } from '@/interfaces';
import displayValues from '@/resources/displayValues.json';
import { translateAmenities } from './HotelDescription';
import { HotelImages } from './HotelImages';
import { HotelTopBar } from './HotelTopBar';
import { NoHotelWarning } from './NoHotelWarning';

interface Props {
    title?: string;
    hotel: Hotel;
    filters: Filters;
}

export const HotelLayout = ({ children, hotel, filters }: PropsWithChildren<Props>) => {
    const { imgPath } = useConfig();
    const router = useRouter();

    useEffect((): void => {
        trackHotelPage(hotel);
    }, []);

    const hotelTitle: string = hotel?.id ? `Pakkereiser til ${hotel.name}` : 'Dette hotellet finnes ikke :(';
    const imagePath: string =
        hotel?.id && hotel.imagePaths && hotel.imagePaths.length > 0
            ? `${imgPath}1280w${hotel.imagePaths[0]}`
            : 'https://www.finn.no/assets/finn-logo-large.png';
    const fullDescription = hotel?.id
        ? `${hotel.name}, ${hotel.destination}, ${hotel.region}, ${hotel.country}
        ${hotel.rating} stjerne
        ${translateAmenities(hotel.facilities, displayValues.amenities.facilities)}
        ${translateAmenities(hotel.activities, displayValues.amenities.activities)}
        ${hotel.distanceToBeach}m fra strand • ${hotel.distanceToCity}m fra sentrum`
        : 'Dette hotellet finnes ikke :(';

    const hotelURL: string = hotel?.id
        ? `https://www.finn.no${router.basePath}/hotell?hotellId=${hotel.id}`
        : 'https://www.finn.no/reise/pakkereiser/resultat';

    return (
        <>
            <Head>
                <title key="title">{hotelTitle}</title>
                <meta name="description" content={fullDescription} />
                <meta name="robots" content="noindex" />
                <meta property="og:description" content={fullDescription} />
                <meta property="og:title" content={hotelTitle} />
                <meta property="og:image" content={imagePath} />
                <meta property="og:url" content={hotelURL} />
                <link rel="canonical" href={hotelURL} />
            </Head>
            <div className="pageholder" data-replace-main-section>
                <div className="grid">
                    {hotel.id ? (
                        <div className="u-mh16">
                            <HotelTopBar hotelName={hotel.name} filters={filters} />
                            <HotelImages hotelId={hotel.id} hotelImages={hotel.imagePaths} />
                            <div className="grid__unit u-size1of1">{children}</div>
                        </div>
                    ) : (
                        <>
                            <HotelTopBar hotelName={' '} filters={filters} />
                            <NoHotelWarning filters={filters} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default connect(({ hotel, filters }: PaleState): Props => ({ hotel, filters }))(HotelLayout);
